import React from 'react';
import { Store, MapPin, Search, Users, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/user/Navbar';
import Footer from '../../../components/user/Footer';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const BusinessDiscoveryPlatform: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [benefitsRef, benefitsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 to-white">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <Link 
            to="/services" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Services
          </Link>
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <Store className="text-primary-600" size={64} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-4 tracking-tight">
                Business Discovery Platform
              </h1>
              <p className="text-xl text-secondary-600">
                Help customers discover your business with our comprehensive listing platform
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-secondary-900 mb-6">
                Get Discovered by More Customers
              </h2>
              <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                Our Business Discovery Platform helps you reach customers who are actively looking 
                for businesses like yours. With location-based search, category filters, and real-time 
                availability, your business becomes easily discoverable.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                Whether you run a restaurant, retail store, salon, or service business, our platform 
                connects you with customers in your area. Showcase your offerings, display your location, 
                and make it easy for customers to find and visit your business.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-12 text-white shadow-soft">
              <Users size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Reach More Customers</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                Join thousands of businesses already using our platform to increase visibility and 
                attract new customers. Get found by users searching for services in your area.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-secondary-600">
              Powerful tools to help customers find you
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Store size={40} />,
                title: 'Business Listings',
                description: 'Create comprehensive business profiles with photos, descriptions, hours, and contact information. Showcase what makes your business unique.'
              },
              {
                icon: <MapPin size={40} />,
                title: 'Location-based Search',
                description: 'Customers can find businesses near them using GPS location. Show up in search results when customers are nearby.'
              },
              {
                icon: <Search size={40} />,
                title: 'Category Management',
                description: 'List your business in relevant categories. Make it easy for customers to find businesses like yours.'
              },
              {
                icon: <CheckCircle size={40} />,
                title: 'Real-time Availability',
                description: 'Update your availability in real-time. Let customers know when you\'re open and ready to serve them.'
              },
              {
                icon: <Users size={40} />,
                title: 'Customer Reviews',
                description: 'Build trust with customer reviews and ratings. Showcase positive feedback and respond to customer concerns.'
              },
              {
                icon: <MapPin size={40} />,
                title: 'Integrated Navigation',
                description: 'Customers can get directions directly to your business. Make it easy for them to visit you.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  featuresVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            benefitsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Why Use Our Discovery Platform?
            </h2>
            <p className="text-xl text-secondary-600">
              Benefits that help your business grow
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Increased Visibility',
                description: 'Get discovered by customers actively searching for businesses like yours. Appear in search results when it matters most.'
              },
              {
                title: 'Free Marketing',
                description: 'Your business listing acts as free marketing. Reach potential customers without expensive advertising campaigns.'
              },
              {
                title: 'Better Customer Targeting',
                description: 'Attract customers who are already interested in your services. Location-based discovery ensures relevant leads.'
              },
              {
                title: 'Easy Management',
                description: 'Update your business information anytime from your dashboard. Keep your listing current and accurate.'
              },
              {
                title: 'Analytics Insights',
                description: 'Track how customers find you. See search queries, views, and customer interactions to optimize your listing.'
              },
              {
                title: 'Competitive Advantage',
                description: 'Stand out from competitors with a professional listing, photos, and customer reviews. Build trust and credibility.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 transition-all duration-500 ${
                  benefitsVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <CheckCircle className="text-primary-500 mr-3" size={24} />
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Ready to Get Discovered?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            List your business today and start attracting more customers
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
            >
              Contact Sales
            </Link>
            <Link
              to="/services"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessDiscoveryPlatform;

